import { useRef, useState } from 'react';

import { useFormikForm } from '#/common/hooks';
import { apiEndpoints, usePostMutation } from '#/services';
import { MAX_FILE_SIZE, SUBMIT_FORM_TIMEOUT, VALIDATION_MESSAGES } from '#/utils/constants';
import {
  generateValidationSchema,
  getFileNameFromUrl,
  replaceWhiteSpaceWithUnderscore,
  toSnakeCase,
} from '#/utils/helperFunctions';

export function useLeadForm(chatbotData, faqQuestionId, setFaqQuestionId, alert) {
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [fileFieldName, setFileFieldName] = useState('');
  const [isFieldSubmit, setIsFieldSubmit] = useState(false);
  const [fileQueue, setFileQueue] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [isProcessingFiles, setIsProcessingFiles] = useState(false);
  const [isSubmissionSuccess, setIsSubmissionSuccess] = useState(false);

  const fileRef = useRef(null);

  const faqs = chatbotData?.data?.setting?.faqs || [];
  const renderLeadForm = faqs?.find(item => item?.id === faqQuestionId);

  const onUploadFileSuccess = ({ data }) => {
    setUploadedFiles(prev => {
      const existing = prev[fileFieldName] || [];
      return {
        ...prev,
        [fileFieldName]: [...existing, data?.file],
      };
    });

    processNextFile();
  };

  const onError = ({ response: { data } = {} }) => {
    const errorMessage =
      data?.image?.[0] ||
      data?.detail ||
      data?.non_field_errors?.[0] ||
      data?.chatbot_lead?.[0] ||
      VALIDATION_MESSAGES.SUBMISSION_ERROR;

    alert.error(errorMessage);
    processNextFile();
  };

  const { mutate: uploadFile, isPending: isUploadFileLoading } = usePostMutation({
    url: apiEndpoints.UPLOAD_FILE,
    onSuccess: onUploadFileSuccess,
    onError,
  });

  const onLeadFormSuccess = () => {
    setIsFieldSubmit(false);
    setIsSubmissionSuccess(true);
    setTimeout(() => {
      handleLeadFormClose();
      setIsSubmissionSuccess(false);
    }, SUBMIT_FORM_TIMEOUT);
  };

  const { mutate: onLeadSubmit, isPending: isLeadSubmitLoading } = usePostMutation({
    url: apiEndpoints.LEAD_RECORD,
    onSuccess: onLeadFormSuccess,
    onError,
  });

  const generateInitialValues = fields =>
    fields?.reduce((acc, field) => {
      if (field?.label) acc[field.label] = '';
      return acc;
    }, {}) || {};

  const onLeadFormSubmit = values => {
    const payload = {
      chatbotLead: renderLeadForm?.lead?.id,
      response: toSnakeCase({ ...values, ...uploadedFiles }),
    };
    onLeadSubmit({ payload });
    resetForm();
    setIsFieldSubmit(true);
    setUploadedFiles({});
  };

  const leadFormik = useFormikForm(
    generateInitialValues(renderLeadForm?.lead?.fields),
    onLeadFormSubmit,
    generateValidationSchema(renderLeadForm?.lead?.fields)
  );

  const { setFieldValue, resetForm } = leadFormik;

  const processNextFile = () => {
    const nextIndex = currentFileIndex + 1;
    setCurrentFileIndex(nextIndex);

    if (nextIndex < fileQueue.length) {
      const { file, fieldName } = fileQueue[nextIndex];
      const formData = new FormData();
      formData.append('file', file, file.name);
      setFileFieldName(fieldName);
      uploadFile({ payload: formData });
    } else {
      setIsProcessingFiles(false);
      setFileQueue([]);
      setCurrentFileIndex(0);
    }
  };

  const handleFileChange = fieldName => e => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    const files = Array.from(fileList);
    const newFileQueue = [];
    let hasOversized = false;
    let hasDuplicate = false;

    const existingFileNames = uploadedFiles[fieldName]?.map(item => getFileNameFromUrl(item));

    for (let file of files) {
      const fileName = replaceWhiteSpaceWithUnderscore(file.name);
      if (existingFileNames?.includes(fileName)) {
        hasDuplicate = true;
        alert.error(VALIDATION_MESSAGES.DUPLICATE_FILE);
        continue;
      }

      if (file.size > MAX_FILE_SIZE) {
        hasOversized = true;
        alert.error(`${file.name} - ${VALIDATION_MESSAGES.FILE_TOO_LARGE}`);
        continue;
      }

      newFileQueue.push({ file, fieldName });
    }

    if ((hasOversized || hasDuplicate) && newFileQueue.length === 0) {
      setIsFieldSubmit(!isFieldSubmit);
      return;
    }

    setFileQueue(prev => [...prev, ...newFileQueue]);
    setCurrentFileIndex(fileQueue.length);
    setIsProcessingFiles(true);

    setFieldValue(fieldName, files);

    if (newFileQueue.length > 0) {
      const { file, fieldName: firstFieldName } = newFileQueue[0];
      const formData = new FormData();
      formData.append('file', file, file.name);
      setFileFieldName(firstFieldName);
      uploadFile({ payload: formData });
    }

    if (fileRef.current) {
      fileRef.current.value = '';
    }
  };

  const handleRemoveFile = (fieldName, fileUrl) => () => {
    setUploadedFiles(prev => {
      const existing = prev[fieldName];
      if (!existing) return prev;

      const updated = existing.filter(f => f !== fileUrl);
      if (updated.length === 0) {
        const copy = { ...prev };
        delete copy[fieldName];
        setFieldValue(fieldName, '');
        return copy;
      }

      setFieldValue(fieldName, updated);
      return { ...prev, [fieldName]: updated };
    });
  };

  const handleTelInputChange = fieldName => e => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setFieldValue(fieldName, value);
    }
  };

  const handleSelectChange = (fieldName, value) => {
    setFieldValue(fieldName, value);
  };

  const handleLeadFormClose = () => {
    setFaqQuestionId('');
    setUploadedFiles({});
    setIsSubmissionSuccess(false);
  };

  return {
    faqQuestionId,
    uploadedFiles,
    setUploadedFiles,
    isFieldSubmit,
    setIsFieldSubmit,
    leadFormik,
    isUploadFileLoading,
    handleFileChange,
    handleTelInputChange,
    handleLeadFormClose,
    handleRemoveFile,
    handleSelectChange,
    renderLeadForm,
    isProcessingFiles,
    fileRef,
    isLeadSubmitLoading,
    isSubmissionSuccess,
  };
}
